import { Injectable } from '@angular/core';
import { IDocumentType } from '../message-models/document-type.interface';
import { IJobTitleInfo } from '../message-models/job-title.interface';
import { Observable, Subject, of } from 'rxjs';
import { CachedData } from '../common/cached-data.class';
import { BaseApiService } from '../common/base-api.service';
import { HttpClient } from '@angular/common/http';
import { SettingsProviderService } from '../common/settings-provider.service';
import { IRiskInfo } from '../message-models/risk-info.interface';
import { IJobLevelInfo } from '../message-models/job-level.interface';
import { IRiskMatrixEntry } from '../message-models/risk-matrix-entry.interface';
import { IEmploymentStatus } from '../message-models/employment-status.interface';
import { IOperativeTypeInfo } from '../message-models/operative-type.interface';
import { ITrainingTypeCategoryInfo } from '../message-models/trainning-record-category.interface';
import { IBusinessUnitInfo } from '../message-models/business-unit.interface';

@Injectable({
  providedIn: 'root'
})
export class ListDataService extends BaseApiService {

  private documentTypeData: CachedData<IDocumentType> = new CachedData<IDocumentType>([], 100000);
  private jobTitleData: CachedData<IJobTitleInfo> = new CachedData<IJobTitleInfo>([], 100000);
  private riskData: CachedData<IRiskInfo> = new CachedData<IRiskInfo>([], 100000);
  private employmentStatusData: CachedData<IEmploymentStatus> = new CachedData<IEmploymentStatus>([], 100000);
  private jobLevelData: CachedData<IJobLevelInfo> = new CachedData<IJobLevelInfo>([], 100000);
  private riskMatrixData: CachedData<IRiskMatrixEntry> = new CachedData<IRiskMatrixEntry>([], 100000);
  private operativeTypeData: CachedData<IOperativeTypeInfo> = new CachedData<IOperativeTypeInfo>([], 100000);
  private trainTypeCategoryData: CachedData<ITrainingTypeCategoryInfo> = new CachedData<ITrainingTypeCategoryInfo>([], 100000);
  private businessUnitData: CachedData<IBusinessUnitInfo> = new CachedData<IBusinessUnitInfo>([], 100000);

  // Create a private Subject that will act as a trigger
  private triggerListUpdate = new Subject<void>();

  constructor(http: HttpClient) {
    super(http, new SettingsProviderService('ecaApi'), 'ListData');
  }

  getAllDocumentTypes(): Observable<IDocumentType[]> {
    if (this.documentTypeData && this.documentTypeData.isDataFresh()) {
      return of(this.documentTypeData.data);
    } else {
      const observedResult = this.post<IDocumentType[]>('GetAllDocumentTypes');
      observedResult.subscribe(
        {
          next: results => {
            this.documentTypeData.clear();
            results.forEach(r => {
              this.documentTypeData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getAllJobTitles(): Observable<IJobTitleInfo[]> {
    if (this.jobTitleData && this.jobTitleData.isDataFresh()) {
      return of(this.jobTitleData.data);
    } else {
      const observedResult = this.post<IJobTitleInfo[]>('GetAllJobTitles');
      observedResult.subscribe(
        {
          next: results => {
            this.jobTitleData.clear();
            results.forEach(r => {
              this.jobTitleData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getJobTitlesForOperativeType(operativeTypeId: string): Observable<IJobTitleInfo[]> {
    if (this.jobTitleData && this.jobTitleData.isDataFresh()) {
      return of(this.jobTitleData.data);
    } else {
      const observedResult = this.post<IJobTitleInfo[]>('GetJobTitlesForOperativeType', { id: operativeTypeId });
      observedResult.subscribe(
        {
          next: results => {
            this.jobTitleData.clear();
            results.forEach(r => {
              this.jobTitleData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getAllOperativeTypes(): Observable<IOperativeTypeInfo[]> {
    if (this.operativeTypeData && this.operativeTypeData.isDataFresh()) {
      return of(this.operativeTypeData.data);
    } else {
      const observedResult = this.post<IOperativeTypeInfo[]>('GetAllOperativeTypes');
      observedResult.subscribe(
        {
          next: results => {
            this.operativeTypeData.clear();
            results.forEach(r => {
              this.operativeTypeData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getBusinessUnitsForCompany(companyId: string): Observable<IBusinessUnitInfo[]> {
    if (this.businessUnitData && this.businessUnitData.isDataFresh()) {
      return of(this.businessUnitData.data);
    } else {
      const observedResult = this.post<IBusinessUnitInfo[]>('GetBusinessUnitsForCompany', { companyId });
      observedResult.subscribe(
        {
          next: results => {
            this.businessUnitData.clear();
            results.forEach(r => {
              this.businessUnitData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getIsBusinessUnitInUse(BusinessUnitId: string): Observable<boolean> {
    return this.post<boolean>('GetIsBusinessUnitInUse', { BusinessUnitId });
  }

  getAllEmploymentStatuses(): Observable<IEmploymentStatus[]> {
    if (this.employmentStatusData && this.employmentStatusData.isDataFresh()) {
      return of(this.employmentStatusData.data);
    } else {
      const observedResult = this.post<IEmploymentStatus[]>('GetAllEmploymentStatuses');
      observedResult.subscribe(
        {
          next: results => {
            this.employmentStatusData.data = results;
            results.forEach(r => {
              this.employmentStatusData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getAllRisks(): Observable<IRiskInfo[]> {
    if (this.riskData && this.riskData.isDataFresh()) {
      return of(this.riskData.data);
    } else {
      const observedResult = this.post<IRiskInfo[]>('GetAllRisks');
      observedResult.subscribe(
        {
          next: results => {
            this.riskData.data = results;
            results.forEach(r => {
              this.riskData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getAllJobLevels(): Observable<IJobLevelInfo[]> {
    if (this.jobLevelData && this.jobLevelData.isDataFresh()) {
      return of(this.jobLevelData.data);
    } else {
      const observedResult = this.post<IJobLevelInfo[]>('GetAllJobLevels');
      observedResult.subscribe(
        {
          next: results => {
            this.jobLevelData.clear();
            results.forEach(r => {
              this.jobLevelData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getRiskMatrix(): Observable<IRiskMatrixEntry[]> {
    if (this.riskMatrixData && this.riskMatrixData.isDataFresh()) {
      return of(this.riskMatrixData.data);
    } else {
      const observedResult = this.post<IRiskMatrixEntry[]>('GetRiskMatrix');
      observedResult.subscribe(
        {
          next: results => {
            this.riskMatrixData.clear();
            results.forEach(r => {
              this.riskMatrixData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  getAllTrainTypeCategory(): Observable<ITrainingTypeCategoryInfo[]> {
    if (this.trainTypeCategoryData && this.trainTypeCategoryData.isDataFresh()) {
      return of(this.trainTypeCategoryData.data);
    } else {
      const observedResult = this.post<ITrainingTypeCategoryInfo[]>('GetAllTrainTypeCategory');
      observedResult.subscribe(
        {
          next: results => {
            this.trainTypeCategoryData.clear();
            results.forEach(r => {
              this.trainTypeCategoryData.addData(r);
            });
          }
        });
      return observedResult;
    }
  }

  createOrUpdateBusinessUnit(businessUnit: IBusinessUnitInfo) {
    return this.post<IBusinessUnitInfo>('CreateOrUpdateBusinessUnit', businessUnit);
  }

  deleteBusinessUnit(businessUnit: IBusinessUnitInfo) {
    return this.post<IBusinessUnitInfo>('DeleteBusinessUnit', businessUnit);
  }

  // Expose the observable (stream) part of the Subject so other components can subscribe to it
  updateListRequired = this.triggerListUpdate.asObservable();

  // A method to trigger the event
  updateLists() {
    // Emit a value to notify all subscribers
    this.triggerListUpdate.next();
  }

}
